import { useState, useEffect, useRef } from 'react'
import { UUID } from "uuidjs";
import useClient from './useClient'
import cloneDeep from 'lodash.clonedeep'

export default function useItems(_locationUUID = null) {
  const [items, setItems] = useState({})
  const [assembledItems, setAssembledItems] = useState({})
  const [categories, setCategories] = useState({})
  const [categoryTree, setCategoryTree] = useState([])
  const [availability, setAvailability] = useState({})
  const [sizes, setSizes] = useState({})
  const [modifiers, setModifiers] = useState({})
  const [eanIndex, setEanIndex] = useState({})
  const [pluIndex, setPluIndex] = useState({})
  const [sizeGroups, setSizeGroups] = useState({})
  const [sizeConnections, setSizeConnections] = useState({})
  const {
    catalogLoading,
    client,
    deviceLocationUUID,
    selectedLocationUUID
  } = useClient()

  const uid = useRef(UUID.generate())
  const _currentLocationUUID = useRef(null)
  const [locationUUID, setLocationUUID] = useState(null)

  const onLocationAvailabilityUpdated = (data) => {
    if (data.locationUUID === _currentLocationUUID.current) {
      setAssembledItems(cloneDeep(client.catalog.assembledItems?.[_currentLocationUUID.current]))
      setCategoryTree(cloneDeep((client.catalog.locationCategoryTree[_currentLocationUUID.current] || [])))
      setAvailability(cloneDeep(client.catalog.availability[_currentLocationUUID.current]))
      if(Object.keys(categories).length === 0) {
        setCategories(cloneDeep(client.catalog.categories))
      }
    }
  }

  useEffect(() => {
    _currentLocationUUID.current = locationUUID
  }, [locationUUID])

  useEffect(() => {
    if (selectedLocationUUID != null || deviceLocationUUID != null || _locationUUID != null) {
      let newLocationUUID = deviceLocationUUID != null
        ? deviceLocationUUID // Primary location decision by device token
        : _locationUUID != null
          ? _locationUUID // Free location decision per hook
          : selectedLocationUUID // Secondary location decision from context
      setLocationUUID(newLocationUUID)
    }
  }, [_locationUUID, deviceLocationUUID, selectedLocationUUID])

  useEffect(() => {
    if (client != null && !catalogLoading && locationUUID != null) {
      setItems(cloneDeep(client.catalog.items))
      setSizes(cloneDeep(client.catalog.sizes))
      setModifiers(cloneDeep(client.catalog.modifiers))
      setCategories(cloneDeep(client.catalog.categories))
      setCategoryTree(cloneDeep((client.catalog.locationCategoryTree[locationUUID] || [])))
      setEanIndex(cloneDeep(client.catalog.eanIndex || {}))
      setPluIndex(cloneDeep(client.catalog.pluIndex || {}))
      setAvailability(cloneDeep(client.catalog.availability[locationUUID]))
      setAssembledItems(cloneDeep(client.catalog.assembledItems?.[locationUUID]))
      setSizeGroups(cloneDeep(client.catalog.sizeGroups))
      setSizeConnections(cloneDeep(client.catalog.sizeConnections))
    }
  }, [catalogLoading, locationUUID, client])

  useEffect(() => {
    if (client != null) {
      client.catalog.addCallback(
        'onLocationAvailabilityUpdated',
        uid.current,
        onLocationAvailabilityUpdated
      )
      setItems(cloneDeep(client.catalog.items))
      setSizes(cloneDeep(client.catalog.sizes))
      setModifiers(cloneDeep(client.catalog.modifiers))
      setCategories(cloneDeep(client.catalog.categories))
      setCategoryTree(cloneDeep(client.catalog.locationCategoryTree[locationUUID] || []))
      setEanIndex(cloneDeep(client.catalog.eanIndex || {}))
      setPluIndex(cloneDeep(client.catalog.pluIndex || {}))
      setAvailability(cloneDeep(client.catalog.availability[locationUUID]))
      setAssembledItems(cloneDeep(client.catalog.assembledItems?.[locationUUID]))
      setSizeGroups(cloneDeep(client.catalog.sizeGroups))
      setSizeConnections(cloneDeep(client.catalog.sizeConnections))
    }
    return () => {
      // ON UNMOUNT
      if (client != null) {
        client.catalog.removeCallback('onLocationAvailabilityUpdated', uid.current)
      }
    }
  }, [client])
  
  const getForcedUpsells = (takeAway) => {
    return client?.catalog?.getForcedUpsells(takeAway, locationUUID)
  }

  return {
    items,
    assembledItems,
    categories,
    categoryTree,
    availability,
    sizes,
    modifiers,
    eanIndex,
    pluIndex,
    sizeGroups,
    sizeConnections,
    getForcedUpsells
  }
}
